import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { OfferedPriceHint, TRANSPORT_LETTERS_TAB_KEY, transportLettersTab } from '@/Containers/TransportationSection/Components'
import { TransportOffersBlock } from '@/Containers/TransportationSection/OfferContainer/Hooks'
import { Form, FormField } from '@/Core/UI/Form'
import { CostCurrencyField } from '@/Ship/Components/CostCurrencyField'
import {
    EntityBody,
    EntityLayout,
    EntityTabs,
    FieldCell,
    FieldGrid,
    FieldSection,
    FileFieldCell,
    SectionEmpty,
} from '@/Ship/Components/EntityCard'

import { RequestCardHeader, RequestClientViewCell, RequestStatusCell } from '../Components/RequestCells'
import { REQUEST_STATUS_CODES, REQUEST_STATUS_RESOURCE } from '../Contracts/IRequest'
import { REQUEST_FIELD_LABELS as L } from '../Contracts/RequestFieldLabels'
import { ApplyRequestCreateDefaultsTask } from '../Tasks/ApplyRequestCreateDefaultsTask'

import type { RequestCrudContextLike, RequestFormContextLike } from '../Contracts/IRequest'
import type { ReactNode } from 'react'

interface Props {
    crudContext: RequestCrudContextLike | undefined
    formContext: RequestFormContextLike | undefined
}

const STATUS = 'status'
const CLIENT = 'client'
const FILES = 'files'
const PRICE = 'offeredPrice'
const CURRENCY = 'offeredPriceCurrency'

/**
 * Блоки картки (`SPEC-022` §8.3), склад і порядок полів — легасі
 * (`RequestFormTemplate.jsx:23-61`), відповідальний піднятий до назви:
 *
 *  - **Загальне** — назва, клієнт, відповідальний, статус, вантаж, тип авто, опис;
 *  - **Маршрут** — країни й адреси парами в два стовпці;
 *  - **Файли** — і при створенні (G-05);
 *  - **Закриття** — лише в закритому запиті (програш чи створена заявка);
 *  - **Службова інформація** — останньою.
 */
const SECTIONS = {
    general: ['title', CLIENT, 'responsible', STATUS, 'cargo', 'typesCars', 'description'],
    route: ['loadingCountries', 'addressLoading', 'unloadingCountries', 'addressUnloading'],
    closing: ['closingAt', 'lossReason'],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину: довгі тексти. */
const WIDE = new Set<string>(['cargo', 'description'])

/** Ставить сервер — у формі нового запиту їм нема чого показати. */
const SERVER_FIELDS = new Set<string>([STATUS, ...SECTIONS.closing, ...SECTIONS.service])

/** Розставлене по блоках; «Ід» — колонка реєстру й шапка, а не поле картки. */
const PLACED = new Set<string>([...Object.values(SECTIONS).flat(), FILES, PRICE, CURRENCY, 'id'])

/** Стани, у яких запит закрито — тоді є блок «Закриття». */
const CLOSED_CODES = new Set<string>([REQUEST_STATUS_CODES.loss, REQUEST_STATUS_CODES.application])

/** Значення за замовчуванням форми створення — один раз на відкриту форму. */
const RequestCreateDefaults = ({ formContext }: { formContext: RequestFormContextLike }) => {
    useEffect(() => {
        void ApplyRequestCreateDefaultsTask(formContext)
    }, [formContext])

    return null
}

/**
 * Ціна з валютою і підказка над нею (`SPEC-022` §8.3, §12): «1 200,00 EUR» у
 * перегляді, одне поле з валютою — у формі.
 */
const OfferedPrice = ({ formContext }: { formContext: RequestFormContextLike }) => (
    <>
        <OfferedPriceHint
            statusResource={REQUEST_STATUS_RESOURCE}
            codes={{ price: REQUEST_STATUS_CODES.price, new: REQUEST_STATUS_CODES.new }}
            subject={{ new: 'новий запит', closed: 'закритому запиті' }}
        />
        <CostCurrencyField formContext={formContext} amount={PRICE} currency={CURRENCY} label={L.offeredPrice} />
    </>
)

/**
 * Картка запиту на спільних правилах карток (`EntityLayout`, як картка
 * клієнта): шапка «Запит: <клієнт>» зі статусом, блоки полів двома стовпцями.
 *
 * Праворуч — блок «Цінові пропозиції» (Hook `OfferContainer`; у формі
 * створення — пояснення, коли їх можна буде додати) і під ним
 * «Запропонована ціна». Вкладка «Листи» (провайдер `request`) з'явиться поруч
 * із «Загальним» у Н-025.2.
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid` і код
 * статусу (блок «Закриття»): інакше ядровий `FormField` перемонтовував би всі
 * поля на кожну літеру. Живі значення показують observer-компоненти
 * (`RequestCardHeader`, `RequestStatusCell`, `CostCurrencyField`).
 */
export const RequestFormTemplate = observer(({ crudContext, formContext }: Props) => {
    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''
    const [lettersEpoch, setLettersEpoch] = useState(0)
    const isEditable = formContext?.isEditable === true
    const statusCode = formContext?.values?.status?.code
    const isClosed = typeof statusCode === 'string' && CLOSED_CODES.has(statusCode)

    // Поля, додані в ресурс пізніше, не мають зникати мовчки
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode => {
        if (isNew && SERVER_FIELDS.has(name)) return null

        if (name === STATUS) {
            return formContext ? (
                <FieldCell key={name}>
                    <RequestStatusCell formContext={formContext} />
                </FieldCell>
            ) : null
        }

        if (name === CLIENT && !isEditable && formContext) {
            return (
                <FieldCell key={name}>
                    <RequestClientViewCell formContext={formContext} />
                </FieldCell>
            )
        }

        if (!(name in fields)) return null

        return (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        )
    }

    const grid = (names: readonly string[]) => (
        <Form formContext={formContext}>
            <FieldGrid>{names.map(cell)}</FieldGrid>
        </Form>
    )

    const main = (
        <>
            <FieldSection title="Загальне">{grid(SECTIONS.general)}</FieldSection>
            <FieldSection title="Маршрут">{grid(SECTIONS.route)}</FieldSection>
            <FieldSection title={L.files}>
                <Form formContext={formContext}>
                    <FieldGrid>
                        <FieldCell wide>
                            <FileFieldCell formContext={formContext} name={FILES} label={L.files} />
                        </FieldCell>
                    </FieldGrid>
                </Form>
            </FieldSection>
            <FieldSection title="Закриття" hidden={isNew || !isClosed}>
                {grid(SECTIONS.closing)}
            </FieldSection>
            <FieldSection title="Додатково" hidden={rest.length === 0}>
                {grid(rest)}
            </FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    const aside = (
        <>
            {isNew ? (
                <FieldSection title="Цінові пропозиції">
                    <SectionEmpty>Пропозиції можна буде додати після збереження запиту.</SectionEmpty>
                </FieldSection>
            ) : (
                <TransportOffersBlock kind="request" owner={formContext} />
            )}
            {formContext && PRICE in fields && CURRENCY in fields ? (
                <FieldSection title="Ціна">
                    <Form formContext={formContext}>
                        <OfferedPrice formContext={formContext} />
                    </Form>
                </FieldSection>
            ) : null}
        </>
    )

    if (isNew || !formContext) {
        return (
            <EntityLayout>
                {formContext ? <RequestCreateDefaults formContext={formContext} /> : null}
                <EntityBody main={main} aside={aside} />
            </EntityLayout>
        )
    }

    return (
        <EntityLayout>
            <EntityTabs
                header={<RequestCardHeader formContext={formContext} />}
                onChange={(key) => {
                    if (key === TRANSPORT_LETTERS_TAB_KEY) setLettersEpoch((epoch) => epoch + 1)
                }}
                items={[
                    { key: 'general', label: 'Загальне', children: <EntityBody main={main} aside={aside} /> },
                    ...transportLettersTab('request', uuid, lettersEpoch),
                ]}
            />
        </EntityLayout>
    )
})
